import { deleteAluno, getAlunos, getContrato } from "@/services/aluno";
import { Button, ConfigProvider, Input, Modal, Popconfirm, Select, Space, Table, TableProps, message, theme } from "antd";
import { Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "./styles";
import { PrinterOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Contrato } from "../Cadastro/Reports/Contrato";
import { usePermissions } from "@/hooks/usePermissions";
import { errorHandler } from "@/utils/errorhandler";
import { getGradesHorarias, getGradeHorariaById } from "@/services/gradeHoraria";
import { filtrarAlunos } from "@/utils/filtroAlunos";
import { numeroDoAluno } from "@/utils/numeroAluno";

export default function ConsultaAluno() {
  const [alunos, setAlunos] = useState([]);
  const [isModalContratoOpen, setIsModalContratoOpen] = useState<boolean>(false);
  const [dadosContrato, setDadosContrato] = useState<any>();
  const [gradeHoraria, setGradeHoraria] = useState<any>(null);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  // Vazio = todos os status. O filtro de status saiu do campo de texto e virou
  // uma seleção própria, que aceita um, vários ou nenhum status.
  const [statusSelecionados, setStatusSelecionados] = useState<string[]>([]);
  const { canInsert, canEdit, isMaster } = usePermissions();

  const handleAddAluno = useCallback(() => {
    return navigate("/aluno/cadastro");
  }, [navigate]);

  const handleGetAluno = async () => {
    const response: any = await getAlunos();
    if (response.status == 200) {
      setAlunos(response?.data);
    }
    return;
  };

  const editAluno = async (id: number) => {
    return navigate(`/aluno/${id}`);
  };

  // Exclusão física: apaga a matrícula inteira (específicas, serviços, pagamentos,
  // cheques e boletos). As Pessoas do aluno e do responsável são preservadas pela API.
  const excluirAluno = async (id: number) => {
    try {
      await deleteAluno(id);
      message.success('Aluno excluído com sucesso!');
      await handleGetAluno();
    } catch (error) {
      errorHandler(error);
    }
  };

  useEffect(() => {
    handleGetAluno();
  }, []);

  const columns: TableProps<any>['columns'] = [
    {
      // Sequencial próprio do orçamento ou da matrícula, não o Id da tabela.
      title: 'Id',
      key: 'id',
      render: (_, record) => numeroDoAluno(record),
    },
    {
      // Orçamento não tem número de matrícula: a API devolve nulo e a coluna
      // fica vazia.
      title: 'Matricula',
      dataIndex: 'matricula',
      key: 'matricula',
    },
    {
      title: 'Nome',
      dataIndex: 'nome',
      key: 'nome',
    },
    {
      title: 'CPF',
      dataIndex: 'cpf',
      key: 'cpf',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Celular',
      dataIndex: 'celular',
      key: 'celular',
    },
    {
      title: 'Unidade',
      dataIndex: 'unidadeNome',
      key: 'unidadeNome',
    },
    {
      title: 'Turma',
      dataIndex: 'turmaNome',
      key: 'turmaNome',
    },
    {
      title: 'Status',
      dataIndex: 'statusNome',
      key: 'statusNome',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {canEdit(5) && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => editAluno(record.id)}
            />
          )}
          {record.statusNome === 'Matriculado' && (
            <Button
              type="link"
              icon={<PrinterOutlined />}
              onClick={() => printAluno(record.id)}
            />
          )}
          {isMaster && (
            <Popconfirm
              title="Confirma a exclusão do aluno?"
              description="Todos os dados da matrícula (específicas, serviços, pagamentos, cheques e boletos) serão apagados e o aluno sairá dos relatórios. Esta ação não pode ser desfeita."
              onConfirm={() => excluirAluno(record.id)}
              okText="Sim"
              cancelText="Não"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                title="Excluir aluno"
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const printAluno = async (id: any) => {
    const { data: aluno } = await getContrato(id);
    setDadosContrato(aluno);
    setGradeHoraria(null);

    const turmaId = aluno?.turmaId;
    const unidadeId = aluno?.unidade?.id;
    if (turmaId && unidadeId) {
      try {
        const listaRes = await getGradesHorarias({ turmaId, unidadeId });
        if (listaRes.status === 200 && listaRes.data?.length > 0) {
          const detalheRes = await getGradeHorariaById(listaRes.data[0].id);
          if (detalheRes.status === 200) {
            setGradeHoraria(detalheRes.data);
          }
        }
      } catch {
        // grade não encontrada — não bloqueia a impressão do contrato
      }
    }

    setIsModalContratoOpen(true);
  };

  const handleOk = () => {
    setIsModalContratoOpen(false);
  };

  const handleCancel = () => {
    setIsModalContratoOpen(false);
  };

  const handleSearch = () => {
    // Filtro acontece em tempo real via `filteredAlunos`; mantido para Enter sem efeito adicional.
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // As opções saem dos próprios dados: assim a lista acompanha os status que a
  // API devolve, sem depender de uma tabela fixa no frontend.
  const opcoesStatus = useMemo(() => {
    const nomes = Array.from(
      new Set(
        alunos
          .map((aluno: any) => aluno?.statusNome)
          .filter((nome: unknown): nome is string => typeof nome === 'string' && nome !== '')
      )
    ).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    return nomes.map((nome) => ({ value: nome, label: nome }));
  }, [alunos]);

  const filteredAlunos = filtrarAlunos(alunos, searchTerm, { considerarStatus: false })
    .filter((aluno: any) =>
      statusSelecionados.length === 0 || statusSelecionados.includes(aluno?.statusNome)
    );

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Consulta de Alunos</span>
      </div>
      <ConfigProvider
        theme={{
          algorithm: [theme.compactAlgorithm, theme.darkAlgorithm],
        }}
      >
        <Container>
          <div className="flex gap-8 items-start">
            <Input
              type="text"
              placeholder="Pesquisar por nome, CPF, matrícula, responsável ou dono do cheque"
              className="w-96 mb-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress} // Adicionar evento onKeyPress
            />
            <Select
              mode="multiple"
              allowClear
              className="w-72 mb-8"
              aria-label="Status"
              // Sem nada selecionado a lista mostra todos os status.
              placeholder="Status: Todos"
              value={statusSelecionados}
              onChange={setStatusSelecionados}
              options={opcoesStatus}
              maxTagCount="responsive"
            />
            {canInsert(5) && (
              <Button className="flex gap-2" onClick={handleAddAluno}>
                Adicionar
                <Plus width={18} />
              </Button>
            )}
          </div>
          <div className="grid ">
            <div>
              <Table rowKey="id" columns={columns} dataSource={filteredAlunos}>
              </Table>
            </div>
          </div>
        </Container>
      </ConfigProvider>
      
      <Modal
        title="Contrato"
        open={isModalContratoOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okButtonProps={{ style: { backgroundColor: '#b4915dab', borderColor: '#b4915dab', fontWeight: "bold" } }}
        cancelButtonProps={{ style: { backgroundColor: '#b34242747', borderColor: '#b34242747', fontWeight: "bold", color: 'white' } }}
        width={'80%'}>
        <Contrato matriculaAluno={dadosContrato} gradeHoraria={gradeHoraria} />
      </Modal>

    </>
  );
}
